import React, { useEffect, useMemo, useRef, useState } from 'react';

import Icon from './Icon';

// =============================================================================
//  Filter menu
//
//  The "Filter" buttons on Mandates, Documents and the Dashboard deal sheet
//  shipped disabled with a "Coming soon" tooltip. This turns them on.
//
//  Each page passes a set of fields; the options for each are derived from the
//  rows actually on screen, so the menu can never offer a value that matches
//  nothing, and it stays correct as the data changes. Active filters are
//  counted on the button so the state is visible when the menu is shut.
// =============================================================================

export interface FilterField<T> {
  key: string;
  label: string;
  /** Value to group/filter this row by. Blank/undefined rows are ignored. */
  accessor: (row: T) => string | undefined | null;
}

interface Props<T> {
  rows: T[];
  fields: ReadonlyArray<FilterField<T>>;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

export function applyFilters<T>(
  rows: T[],
  fields: ReadonlyArray<FilterField<T>>,
  value: Record<string, string>
): T[] {
  const active = fields.filter((f) => value[f.key] && value[f.key] !== 'All');
  if (active.length === 0) return rows;
  return rows.filter((row) => active.every((f) => (f.accessor(row) ?? '') === value[f.key]));
}

function FilterMenu<T>({ rows, fields, value, onChange }: Props<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape, like the stage and progress popovers.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Options come from the data on screen, so nothing offered returns zero rows.
  const options = useMemo(() => {
    const map: Record<string, string[]> = {};
    fields.forEach((f) => {
      const seen = new Set<string>();
      rows.forEach((r) => {
        const v = f.accessor(r);
        if (v !== undefined && v !== null && String(v).trim() !== '') seen.add(String(v));
      });
      map[f.key] = Array.from(seen).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
    });
    return map;
  }, [rows, fields]);

  const activeCount = fields.filter((f) => value[f.key] && value[f.key] !== 'All').length;

  return (
    <div className="filter-menu" ref={ref}>
      <button
        className={`btn ghost ${activeCount > 0 ? 'active' : ''}`}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={activeCount > 0 ? `${activeCount} filter(s) applied` : 'Filter this list'}
        aria-expanded={open}
      >
        <Icon name="filter" size={13} />
        Filter
        {activeCount > 0 && <span className="badge">{activeCount}</span>}
      </button>

      {open && (
        <div className="filter-popover" data-v3="true">
          {fields.map((f) => (
            <label key={f.key} className="field">
              <span>{f.label}</span>
              <select
                value={value[f.key] ?? 'All'}
                onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
              >
                <option value="All">All</option>
                {options[f.key]?.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <div className="filter-foot">
            <button
              className="btn ghost"
              type="button"
              disabled={activeCount === 0}
              onClick={() => onChange({})}
            >
              Clear
            </button>
            <button className="btn primary" type="button" onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterMenu;
